import { useState } from 'react'
import { Bell } from 'lucide-react'
import { toast } from 'sonner'
import { FeatureFlags, createTestNotificationSchema } from '@fp3/shared'
import { Button } from '@/components/ui/button'
import { MaskedNumberInput } from '@/components/ui/MaskedNumberInput'
import { Label } from '@/components/ui/label'
import { ConfirmDeleteDialog } from '@/components/ui/confirm-delete-dialog'
import { useFlag } from '@/modules/feature-flags/hooks/useFeatureFlags'
import { NotificationCard } from './NotificationCard'
import { useNotifications } from '../hooks/useNotifications'
import { useUnreadCount } from '../hooks/useUnreadCount'
import { useMarkAllAsRead } from '../hooks/useMarkAllAsRead'
import { useDeleteAllRead } from '../hooks/useDeleteAllRead'
import { useCreateTestNotification } from '../hooks/useCreateTestNotification'

export const NotificationsList = () => {
  const [page, setPage] = useState(1)
  const [deleteReadOpen, setDeleteReadOpen] = useState(false)
  const [testDelay, setTestDelay] = useState<number | undefined>(5)
  const { data, isLoading } = useNotifications(page)
  const { data: unreadData } = useUnreadCount()
  const markAllAsRead = useMarkAllAsRead()
  const deleteAllRead = useDeleteAllRead()
  const createTest = useCreateTestNotification()
  const { enabled: testNotificationsEnabled } = useFlag(FeatureFlags.TEST_NOTIFICATIONS)

  const handleCreateTest = () => {
    const parsed = createTestNotificationSchema.safeParse({ delaySeconds: testDelay })
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? 'Invalid delay')
      return
    }
    const { delaySeconds } = parsed.data
    createTest.mutate(parsed.data, {
      onSuccess: () => {
        toast.success(`Test notification scheduled in ${delaySeconds}s`)
      },
      onError: () => {
        toast.error('Failed to schedule test notification')
      },
    })
  }

  const testForm = testNotificationsEnabled ? (
    <div className="rounded-md border border-dashed p-4">
      <Label htmlFor="notifications-test-delay" className="text-sm font-medium">
        Send a test notification
      </Label>
      <p className="mb-3 text-xs text-muted-foreground">
        Delivered to you after the chosen delay (1–3600 seconds).
      </p>
      <div className="flex items-end gap-2">
        <div className="w-32">
          <MaskedNumberInput
            id="notifications-test-delay"
            min={1}
            max={3600}
            step={1}
            maxFractionDigits={0}
            value={testDelay}
            onChange={setTestDelay}
            data-testid="notifications-test-delay-input"
          />
        </div>
        <Button
          onClick={handleCreateTest}
          disabled={createTest.isPending}
          data-testid="notifications-test-create-button"
        >
          {createTest.isPending ? 'Scheduling...' : 'Create test notification'}
        </Button>
      </div>
    </div>
  ) : null

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Loading notifications...</div>
  }

  if (!data || data.items.length === 0) {
    return (
      <div className="space-y-4">
        {testForm}
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
          <Bell className="h-12 w-12 mb-4" />
          <p className="text-lg font-medium" data-testid="notifications-empty-heading">
            No notifications
          </p>
          <p className="text-sm" data-testid="notifications-empty-subtext">
            You're all caught up
          </p>
        </div>
      </div>
    )
  }

  const unreadCount = unreadData?.count ?? 0

  return (
    <div className="space-y-4">
      {testForm}

      <div className="flex justify-end gap-2">
        {unreadCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => markAllAsRead.mutate()}
            disabled={markAllAsRead.isPending}
            data-testid="notifications-mark-all-read-button"
          >
            Mark all as read
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setDeleteReadOpen(true)}
          data-testid="notifications-list-delete-read-button"
        >
          Delete read
        </Button>
      </div>

      <ConfirmDeleteDialog
        open={deleteReadOpen}
        onOpenChange={setDeleteReadOpen}
        onConfirm={() => {
          deleteAllRead.mutate(undefined, {
            onSuccess: () => {
              setPage(1)
              setDeleteReadOpen(false)
            },
            onError: () => setDeleteReadOpen(false),
          })
        }}
        title="Delete read notifications"
        description="This will permanently remove all read notifications. Unread notifications will not be affected."
        confirmLabel="Delete read"
        isLoading={deleteAllRead.isPending}
      />

      <div className="space-y-2">
        {data.items.map((notification, index) => (
          <NotificationCard key={notification.id} notification={notification} index={index} />
        ))}
      </div>

      {data.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-4">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {data.page} of {data.totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= data.totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  )
}
